import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    Card, CardBody, Typography, Button,
    Input, Textarea, Chip, Dialog, DialogHeader, DialogBody, DialogFooter
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileExcel, faFilePdf,
    faLock, faUnlock, faCheck, faTimes, faHistory,
    faCalculator, faSpinner,
    faExclamationTriangle, faCar,
    faTable, faList, faGrip, faMoneyBillWave, faFolderOpen, faCloudArrowUp, faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import VersionHistoryModal from './VersionHistoryModal';
import ConfirmModal from '../../common/ConfirmModal';
import { generateSavedReport, downloadSavedReport, savedReportsLink } from './savedRemittanceReports';
import { formatMoney } from '../owners/formatMoney';
import { DEFAULT_COMPANY_INFO, fetchCompanyInfo, resolveCompanyLogoUrl } from '../../../utils/companyInfo';
const { rootUrl } = require('../../../config/config');

const customSelectStyles = {
    control: (base) => ({
        ...base,
        backgroundColor: '#f5f5f5',
        borderColor: '#d1d5db',
        '&:hover': {
            borderColor: '#9ca3af'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
        color: state.isSelected ? 'white' : 'black',
        cursor: 'pointer'
    })
};

// Shared button styles (match CarTransactions / MonthlyInvoices)
const BTN_BASE = 'inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_PRIMARY = `${BTN_BASE} px-4 py-2 bg-[#FF5733] text-white hover:bg-[#e64a2b]`;
const BTN_SECONDARY = `${BTN_BASE} px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200`;
const BTN_OUTLINE = `${BTN_BASE} px-4 py-2 border border-[#FF5733] bg-white text-[#FF5733] hover:bg-[#fff1ec]`;

const LAYOUT_TABS = [
    { key: 'columns', label: 'Columns', icon: faTable },
    { key: 'rows', label: 'Rows', icon: faList },
    { key: 'grid', label: 'Grid', icon: faGrip }
];

// system_week end_date is exclusive (the next week's start), so show the day before it.
const formatWeekRange = (week, withYear = false) => {
    if (!week?.start_date || !week?.end_date) return '';
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    end.setDate(end.getDate() - 1);
    const opts = { month: 'short', day: 'numeric' };
    const endOpts = withYear ? { ...opts, year: 'numeric' } : opts;
    return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', endOpts)}`;
};

const OwnerRemittanceReport = () => {
    const { token, user } = useAuth();
    const [owners, setOwners] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(localStorage.getItem('remittance_selected_owner') || '');
    const [selectedWeek, setSelectedWeek] = useState(localStorage.getItem('remittance_selected_week') || '');
    const [reportData, setReportData] = useState(null);
    const [jobCardsData, setJobCardsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [workingDaysOverrides, setWorkingDaysOverrides] = useState({});
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentData, setPaymentData] = useState({
        date: new Date().toISOString().split('T')[0],
        reference: '',
        payment_method: 'MPESA',
        cashbook_account_id: '',
        amount: '',
        notes: ''
    });
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [selectedRemittanceId, setSelectedRemittanceId] = useState(null);
    const selectedWeekInfo = weeks.find(w => w.week === selectedWeek);
    const [layoutMode, setLayoutMode] = useState(localStorage.getItem('remittance_layout_mode') || 'columns'); // 'columns', 'rows', 'grid'

    useEffect(() => {
        localStorage.setItem('remittance_layout_mode', layoutMode);
    }, [layoutMode]);

    const [hoveredTransaction, setHoveredTransaction] = useState({ show: false, transactions: [], type: '', x: 0, y: 0 });
    const [selectedCars, setSelectedCars] = useState(new Set());
    const [, setShowBulkPaymentDialog] = useState(false);
    const [bulkPaymentData, setBulkPaymentData] = useState({
        date: new Date().toISOString().split('T')[0],
        reference: '',
        payment_method: 'MPESA',
        cashbook_account_id: '',
        amount: '',
        notes: ''
    });
    const [, setPaymentPreview] = useState(null);
    const [, setValidatingBatch] = useState(false);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorDetails, setErrorDetails] = useState({ message: '', errors: [] });
    const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
    // Selecting an owner + week only reads the saved figures; recalculating is an explicit, confirmed step.
    const [reportMissing, setReportMissing] = useState(false);
    // Promise-based ConfirmModal: `await askConfirm({...})` resolves to { confirmed, typed }.
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [confirmTyped, setConfirmTyped] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [exportingFormat, setExportingFormat] = useState(null);
    const [exportError, setExportError] = useState('');
    const [savedNotice, setSavedNotice] = useState(null);
    const reportRequestRef = useRef(0);

    const askConfirm = (options) => new Promise((resolve) => {
        setConfirmTyped('');
        setConfirmError('');
        setConfirmDialog({ variant: 'primary', confirmText: 'Confirm', ...options, resolve });
    });

    const closeConfirm = (confirmed) => {
        if (!confirmDialog) return;
        if (confirmed && confirmDialog.requireTyped && !confirmTyped.trim()) {
            setConfirmError(confirmDialog.requireTyped);
            return;
        }
        confirmDialog.resolve({ confirmed, typed: confirmTyped.trim() });
        setConfirmDialog(null);
    };

    useEffect(() => {
        if (selectedOwner) {
            localStorage.setItem('remittance_selected_owner', selectedOwner);
        } else {
            localStorage.removeItem('remittance_selected_owner');
        }
    }, [selectedOwner]);

    useEffect(() => {
        if (selectedWeek) {
            localStorage.setItem('remittance_selected_week', selectedWeek);
        } else {
            localStorage.removeItem('remittance_selected_week');
        }
    }, [selectedWeek]);

    useEffect(() => {
        // Suppress ResizeObserver errors (benign warning from Material Tailwind and horizontal scroll)
        const resizeObserverErr = window.console.error;
        const resizeObserverWarn = window.console.warn;

        window.console.error = (...args) => {
            const firstArg = String(args[0]);
            if (firstArg.includes('ResizeObserver') || firstArg.includes('resize observer')) {
                return;
            }
            resizeObserverErr(...args);
        };

        window.console.warn = (...args) => {
            const firstArg = String(args[0]);
            if (firstArg.includes('ResizeObserver') || firstArg.includes('resize observer')) {
                return;
            }
            resizeObserverWarn(...args);
        };

        // Also suppress at window error level
        const errorHandler = (event) => {
            if (event.message?.includes('ResizeObserver')) {
                event.stopImmediatePropagation();
                event.preventDefault();
                return true;
            }
        };
        window.addEventListener('error', errorHandler, true);

        fetchOwners();
        fetchWeeks();
        fetchCashbookAccounts();
        fetchCompanyInfo(token).then(setCompanyInfo);

        return () => {
            window.console.error = resizeObserverErr;
            window.console.warn = resizeObserverWarn;
            window.removeEventListener('error', errorHandler, true);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOwners = async () => {
        try {
            const response = await axios.get(`${rootUrl}/owners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOwners(response.data || []);
        } catch (error) {
            console.error('Error fetching owners:', error);
        }
    };

    const fetchWeeks = async () => {
        try {
            const response = await axios.get(`${rootUrl}/system/weeks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWeeks(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching weeks:', error);
        }
    };

    const fetchCashbookAccounts = async () => {
        try {
            const result = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (result.data.success) {
                setCashbookAccounts(result.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching cashbook accounts:', error);
        }
    };

    const companyLogoUrl = resolveCompanyLogoUrl(companyInfo);

    const calculateReport = async () => {
        if (!selectedOwner || !selectedWeek) return;
        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/remittance/owner-calculate`,
                {
                    owner_code: selectedOwner,
                    week_key: selectedWeek,
                    processed_by: user?.username || 'system'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                // Check for errors in the response
                if (response.data.hasErrors && response.data.data?.errors) {
                    if (response.data.hasValidationErrors) {
                        // For validation errors, show modern dialog
                        setErrorDetails({
                            message: response.data.message || 'Validation errors detected. Please resolve these issues before proceeding.',
                            errors: response.data.data.errors
                        });
                        setShowErrorDialog(true);
                        // Still fetch report to show current state
                        await fetchReport();
                    } else {
                        // For other non-blocking errors
                        setErrorDetails({
                            message: 'Some cars failed processing, but you can continue with the others:',
                            errors: response.data.data.errors
                        });
                        setShowErrorDialog(true);
                        await fetchReport();
                    }
                } else {
                    // No errors, proceed normally
                    await fetchReport();
                }
            } else {
                // Complete failure
                setErrorDetails({
                    message: response.data.message || 'Error calculating report',
                    errors: response.data.data?.errors || []
                });
                setShowErrorDialog(true);
                // Still try to fetch current report state
                await fetchReport();
            }
        } catch (error) {
            console.error('Error calculating report:', error);
            setErrorDetails({
                message: error.response?.data?.message || error.message,
                errors: error.response?.data?.data?.errors || []
            });
            setShowErrorDialog(true);
            // Even on network error, try to fetch report if we have selects
            if (selectedOwner && selectedWeek) {
                await fetchReport();
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchReport = async () => {
        if (!selectedOwner || !selectedWeek) return;
        const requestId = ++reportRequestRef.current;
        setLoading(true);
        try {
            const response = await axios.get(
                `${rootUrl}/api/remittance/owner-report/${selectedOwner}/${selectedWeek}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // A newer owner/week selection has started loading; drop this response.
            if (requestId !== reportRequestRef.current) return;
            if (response.data.success) {
                setReportMissing(false);
                setReportData(response.data.data);
                const overrides = {};
                response.data.data.cars.forEach(car => {
                    overrides[car.plate_no] = car.working_days;
                });
                setWorkingDaysOverrides(overrides);

                // Fetch job cards for the owner and week
                try {
                    const jobCardsResponse = await axios.get(
                        `${rootUrl}/api/remittance/owner/${selectedOwner}/${selectedWeek}/job-cards`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (jobCardsResponse.data.success) {
                        const jcs = jobCardsResponse.data.data || [];
                        setJobCardsData(jcs);

                        // Compare totals for validation
                        const reportTotal = parseFloat(response.data.data.totals.total_expenses || 0);
                        const jcTotal = jcs.reduce((sum, jc) => sum + parseFloat(jc.total_cost || 0), 0);

                        if (Math.abs(reportTotal - jcTotal) > 0.01) {
                            const diff = Math.abs(reportTotal - jcTotal);
                            setErrorDetails({
                                message: '⚠️ WARNING: Totals Mismatch Detected!',
                                errors: [{
                                    plate_no: 'AUDIT',
                                    error: `Expenses Total in Report (${formatCurrency(reportTotal)}) does not match Job Cards Detail Total (${formatCurrency(jcTotal)}). Difference: ${formatCurrency(diff)}. Please ensure all job cards are correctly assigned to this invoice week.`
                                }]
                            });
                            setShowErrorDialog(true);
                        }
                    }
                } catch (jobCardsError) {
                    console.error('Error fetching job cards:', jobCardsError);
                    setJobCardsData([]);
                }
            }
        } catch (error) {
            if (requestId !== reportRequestRef.current) return;
            if (error.response?.status === 404) {
                // Nothing calculated yet for this owner and week.
                setReportData(null);
                setJobCardsData([]);
                setReportMissing(true);
            } else {
                console.error('Error fetching report:', error);
            }
        } finally {
            if (requestId === reportRequestRef.current) setLoading(false);
        }
    };

    // Load the saved report (no recalculation) whenever the owner or week changes.
    useEffect(() => {
        setSavedNotice(null);
        setExportError('');
        if (selectedOwner && selectedWeek) {
            fetchReport();
        } else {
            reportRequestRef.current += 1;
            setReportData(null);
            setJobCardsData([]);
            setReportMissing(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOwner, selectedWeek]);

    const requestCalculate = async () => {
        if (reportData) {
            const settled = (reportData.cars || []).filter((car) => ['APPROVED', 'PAID'].includes(car.status) || car.is_locked).length;
            const ownerName = owners.find((o) => o.code === selectedOwner)?.name || selectedOwner;
            const { confirmed } = await askConfirm({
                title: `Recalculate week ${selectedWeek}?`,
                description: `This rebuilds ${ownerName}'s figures for week ${selectedWeek} from the current transactions and overwrites the saved numbers for unlocked cars.`
                    + (settled ? ` ${settled} car(s) are locked, approved or paid and will not change.` : '')
                    + ' Saved report files for this week are marked outdated if any number changes.',
                confirmText: 'Recalculate'
            });
            if (!confirmed) return;
        }
        calculateReport();
    };

    const handleUnlock = async (remittanceId) => {
        const isAdmin = (user?.roles?.includes && user.roles.includes('admin')) || user?.is_admin || user?.role === 'admin' || user?.username === 'admin';
        if (!isAdmin) {
            setErrorDetails({ message: 'Permission Denied', errors: [{ plate_no: 'AUTH', error: 'Only admins can unlock remittances' }] });
            setShowErrorDialog(true);
            return;
        }

        const { confirmed, typed: reason } = await askConfirm({
            title: 'Unlock remittance?',
            description: 'This recalculates all values and resets the remittance to PENDING.',
            typedConfirmLabel: 'Reason for unlocking (required)',
            requireTyped: 'Enter a reason to unlock this remittance.',
            confirmText: 'Unlock',
            variant: 'danger'
        });
        if (!confirmed) return;

        try {
            await axios.put(
                `${rootUrl}/api/remittance/${remittanceId}/unlock`,
                { unlocked_by: user?.username || 'system', reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchReport();
            setErrorDetails({
                message: 'Remittance successfully unlocked.',
                errors: [{ plate_no: 'SUCCESS', error: 'Recalculation complete. Status reset to PENDING.' }]
            });
            setShowErrorDialog(true);
        } catch (error) {
            console.error('Error unlocking remittance:', error);
            setErrorDetails({
                message: 'Unlock Failed',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleWorkingDaysChange = async (plateNo, remittanceId, value) => {
        const newValue = parseInt(value) || 0;
        if (newValue < 0 || newValue > 6) return;

        setWorkingDaysOverrides(prev => ({ ...prev, [plateNo]: newValue }));

        try {
            await axios.put(
                `${rootUrl}/api/remittance/${remittanceId}/working-days`,
                {
                    working_days: newValue,
                    edit_by: user?.username || 'system'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchReport();
        } catch (error) {
            console.error('Error updating working days:', error);
            setErrorDetails({
                message: 'Update Failed',
                errors: [{ plate_no: plateNo, error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    const handleProcess = async (remittanceId) => {
        const { confirmed } = await askConfirm({
            title: 'Process remittance?',
            description: 'This processes and locks the remittance. Unlocking it later needs admin permission.',
            confirmText: 'Process & Lock'
        });
        if (!confirmed) return;

        try {
            await axios.put(
                `${rootUrl}/api/remittance/${remittanceId}/process`,
                { processed_by: user?.username || 'system' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchReport();
            setErrorDetails({
                message: 'Action Completed',
                errors: [{ plate_no: 'SUCCESS', error: 'Remittance processed and locked successfully' }]
            });
            setShowErrorDialog(true);
        } catch (error) {
            console.error('Error processing remittance:', error);
            setErrorDetails({
                message: 'Processing Failed',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    const handleApprove = async (remittanceId) => {
        const { confirmed } = await askConfirm({ title: 'Approve remittance?', confirmText: 'Approve' });
        if (!confirmed) return;

        try {
            await axios.put(
                `${rootUrl}/api/remittance/${remittanceId}/approve`,
                { approved_by: user?.username || 'system' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchReport();
            setErrorDetails({
                message: 'Action Completed',
                errors: [{ plate_no: 'SUCCESS', error: 'Remittance approved successfully' }]
            });
            setShowErrorDialog(true);
        } catch (error) {
            console.error('Error approving remittance:', error);
            setErrorDetails({
                message: 'Approval Failed',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    const handleMarkPaid = async () => {
        if (!paymentData.date || !paymentData.payment_method || !paymentData.cashbook_account_id || !paymentData.amount) {
            setErrorDetails({ message: 'Validation Error', errors: [{ plate_no: 'INPUT', error: 'Payment date, method, cashbook account, and amount are required' }] });
            setShowErrorDialog(true);
            return;
        }

        const { confirmed } = await askConfirm({
            title: 'Confirm payment',
            description: `Record a payment of ${formatCurrency(parseFloat(paymentData.amount))} for this remittance?`,
            confirmText: 'Confirm Payment'
        });
        if (!confirmed) return;

        try {
            const payload = {
                batch_reference: paymentData.reference,
                payment_date: paymentData.date,
                payment_method: paymentData.payment_method,
                cashbook_account_id: paymentData.cashbook_account_id,
                amount: parseFloat(paymentData.amount),
                selected_remittance_ids: [selectedRemittanceId],
                paid_by: user?.username || 'system',
                notes: paymentData.notes
            };

            console.log('Single car payment payload:', payload);

            const result = await axios.post(
                `${rootUrl}/api/remittance/confirm-batch`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (result.data.success) {
                setShowPaymentDialog(false);
                setPaymentData({
                    date: new Date().toISOString().split('T')[0],
                    reference: '',
                    payment_method: 'MPESA',
                    cashbook_account_id: '',
                    amount: '',
                    notes: ''
                });
                await fetchReport();
                setErrorDetails({
                    message: 'Payment Successful',
                    errors: [{ plate_no: 'RECEIPT', error: result.data.message }]
                });
                setShowErrorDialog(true);
            } else {
                setErrorDetails({
                    message: 'Payment Failed',
                    errors: [{ plate_no: 'SYSTEM', error: result.data.message }]
                });
                setShowErrorDialog(true);
            }
        } catch (error) {
            console.error('Error marking as paid:', error);
            setErrorDetails({
                message: 'Transaction Error',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    // Auto-generate batch reference for single car payment
    React.useEffect(() => {
        if (paymentData.payment_method && paymentData.date && !paymentData.reference && showPaymentDialog) {
            const method = paymentData.payment_method;
            const date = new Date(paymentData.date);
            const year = date.getFullYear();
            const timestamp = Date.now().toString().slice(-6);

            let prefix = '';
            switch (method) {
                case 'BANK_TRANSFER':
                    prefix = 'BANK-TRF';
                    break;
                case 'CHEQUE':
                    prefix = 'CHK';
                    break;
                case 'MPESA':
                    prefix = 'MPESA';
                    break;
                case 'CASH':
                    prefix = 'CASH';
                    break;
                default:
                    prefix = 'OTHER';
            }

            const reference = `${prefix}-${year}-${timestamp}`;
            setPaymentData(prev => ({ ...prev, reference }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData.payment_method, paymentData.date, showPaymentDialog]);

    // Bulk operation handlers
    const toggleCarSelection = (carId) => {
        const newSelection = new Set(selectedCars);
        if (newSelection.has(carId)) {
            newSelection.delete(carId);
        } else {
            newSelection.add(carId);
        }
        setSelectedCars(newSelection);
    };

    const toggleSelectAll = () => {
        if (selectedCars.size === reportData.cars.length) {
            setSelectedCars(new Set());
        } else {
            setSelectedCars(new Set(reportData.cars.map(car => car.id)));
        }
    };

    const handleBulkProcess = async () => {
        if (selectedCars.size === 0) {
            setErrorDetails({ message: 'Selection Error', errors: [{ plate_no: 'INPUT', error: 'Please select at least one remittance' }] });
            setShowErrorDialog(true);
            return;
        }

        const { confirmed } = await askConfirm({
            title: `Process ${selectedCars.size} remittance(s)?`,
            description: 'They will be locked, and unlocking needs admin permission.',
            confirmText: 'Process & Lock'
        });
        if (!confirmed) return;

        try {
            const selectedIds = Array.from(selectedCars);
            for (const remittanceId of selectedIds) {
                await axios.put(
                    `${rootUrl}/api/remittance/${remittanceId}/process`,
                    { processed_by: user?.username || 'system' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            setSelectedCars(new Set());
            await fetchReport();
            setErrorDetails({
                message: 'Bulk Action Results',
                errors: [{ plate_no: 'RESULT', error: `${selectedIds.length} remittance(s) processed and locked successfully` }]
            });
            setShowErrorDialog(true);
        } catch (error) {
            console.error('Error bulk processing remittances:', error);
            setErrorDetails({
                message: 'Bulk Processing Error',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    const handleBulkApprove = async () => {
        if (selectedCars.size === 0) {
            setErrorDetails({ message: 'Selection Error', errors: [{ plate_no: 'INPUT', error: 'Please select at least one remittance' }] });
            setShowErrorDialog(true);
            return;
        }

        const { confirmed } = await askConfirm({ title: `Approve ${selectedCars.size} remittance(s)?`, confirmText: 'Approve' });
        if (!confirmed) return;

        try {
            const selectedIds = Array.from(selectedCars);
            for (const remittanceId of selectedIds) {
                await axios.put(
                    `${rootUrl}/api/remittance/${remittanceId}/approve`,
                    { approved_by: user?.username || 'system' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            setSelectedCars(new Set());
            await fetchReport();
            setErrorDetails({
                message: 'Bulk Action Results',
                errors: [{ plate_no: 'RESULT', error: `${selectedIds.length} remittance(s) approved successfully` }]
            });
            setShowErrorDialog(true);
        } catch (error) {
            console.error('Error bulk approving remittances:', error);
            setErrorDetails({
                message: 'Bulk Approval Error',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleBulkMarkPaid = async () => {
        if (selectedCars.size === 0) {
            setErrorDetails({ message: 'Selection Error', errors: [{ plate_no: 'INPUT', error: 'Please select at least one remittance' }] });
            setShowErrorDialog(true);
            return;
        }

        if (!bulkPaymentData.date || !bulkPaymentData.payment_method || !bulkPaymentData.cashbook_account_id || !bulkPaymentData.amount) {
            setErrorDetails({ message: 'Validation Error', errors: [{ plate_no: 'INPUT', error: 'Payment date, method, cashbook account, and amount are required' }] });
            setShowErrorDialog(true);
            return;
        }

        const { confirmed } = await askConfirm({
            title: 'Confirm payment batch',
            description: `Record payment for ${selectedCars.size} remittance(s)?`,
            confirmText: 'Confirm Payment'
        });
        if (!confirmed) return;

        try {
            const selectedIds = Array.from(selectedCars);
            const payload = {
                batch_reference: bulkPaymentData.reference,
                payment_date: bulkPaymentData.date,
                payment_method: bulkPaymentData.payment_method,
                cashbook_account_id: bulkPaymentData.cashbook_account_id,
                amount: parseFloat(bulkPaymentData.amount),
                selected_remittance_ids: selectedIds,
                paid_by: user?.username || 'system',
                notes: bulkPaymentData.notes
            };

            console.log('All form data before submit:', bulkPaymentData);
            console.log('Confirm batch payload:', payload);
            console.log('Payload validation:', {
                batch_reference: { value: payload.batch_reference, valid: !!payload.batch_reference },
                payment_date: { value: payload.payment_date, valid: !!payload.payment_date },
                payment_method: { value: payload.payment_method, valid: !!payload.payment_method },
                cashbook_account_id: { value: payload.cashbook_account_id, valid: !!payload.cashbook_account_id },
                amount: { value: payload.amount, valid: payload.amount > 0 },
                selected_remittance_ids: { value: payload.selected_remittance_ids, valid: payload.selected_remittance_ids.length > 0 },
                paid_by: { value: payload.paid_by, valid: !!payload.paid_by }
            });

            const result = await axios.post(
                `${rootUrl}/api/remittance/confirm-batch`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (result.data.success) {
                setSelectedCars(new Set());
                setShowBulkPaymentDialog(false);
                setBulkPaymentData({
                    date: new Date().toISOString().split('T')[0],
                    reference: '',
                    payment_method: 'MPESA',
                    cashbook_account_id: '',
                    amount: '',
                    notes: ''
                });
                setPaymentPreview(null);
                await fetchReport();
                setErrorDetails({
                    message: 'Batch Processing Complete',
                    errors: [{ plate_no: 'BATCH', error: `Successfully posted ${result.data.allocations_count} allocations to ledger.` }]
                });
                setShowErrorDialog(true);
            } else {
                setErrorDetails({
                    message: 'Batch Payment Failed',
                    errors: [{ plate_no: 'SYSTEM', error: result.data.message }]
                });
                setShowErrorDialog(true);
            }
        } catch (error) {
            console.error('Error confirming payment batch:', error);
            setErrorDetails({
                message: 'Batch Transaction Error',
                errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }]
            });
            setShowErrorDialog(true);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handlePreparePayment = async () => {
        if (selectedCars.size === 0) {
            setErrorDetails({ message: 'Selection Error', errors: [{ plate_no: 'INPUT', error: 'Please select at least one remittance' }] });
            setShowErrorDialog(true);
            return;
        }

        if (!bulkPaymentData.date || !bulkPaymentData.payment_method || !bulkPaymentData.cashbook_account_id || !bulkPaymentData.amount) {
            alert('Payment date, method, cashbook account, and amount are required');
            return;
        }

        setValidatingBatch(true);
        try {
            const selectedIds = Array.from(selectedCars);
            // Calculate total from selected remittances
            const selectedRemittances = reportData?.cars?.filter(r => selectedIds.includes(r.id)) || [];
            const batch_total = selectedRemittances.reduce((sum, r) => sum + (parseFloat(r.owner_payout) || 0), 0);

            console.log('Validation request:', {
                selected_remittance_ids: selectedIds,
                batch_total: batch_total,
                selectedCars: Array.from(selectedCars),
                reportRemittances: reportData?.remittances?.length
            });

            const result = await axios.post(
                `${rootUrl}/api/remittance/allocate-payments`,
                {
                    selected_remittance_ids: selectedIds,
                    batch_total: batch_total
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (result.data.success) {
                setPaymentPreview(result.data);
            } else {
                setErrorDetails({ message: 'Validation Failed', errors: [{ plate_no: 'AUDIT', error: result.data.message }] });
                setShowErrorDialog(true);
            }
        } catch (error) {
            console.error('Error validating payment batch:', error);
            setErrorDetails({ message: 'Execution Error', errors: [{ plate_no: 'SYSTEM', error: error.response?.data?.message || error.message }] });
            setShowErrorDialog(true);
        } finally {
            setValidatingBatch(false);
        }
    };

    const formatCurrency = formatMoney;

    // Transaction tooltip component
    const TransactionTooltip = ({ transactions, type, x, y }) => {
        if (!transactions || transactions.length === 0) return null;

        const formatDate = (dateString) => {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        };

        return (
            <div
                className="fixed z-[9999] bg-white rounded-lg shadow-2xl border-2 border-[#FF5733] min-w-[320px] max-w-md"
                style={{
                    left: `${x + 15}px`,
                    top: `${y + 15}px`,
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-[#FF5733] text-white px-4 py-2 rounded-t-lg sticky top-0">
                    <div className="flex justify-between items-center">
                        <span className="font-bold">{type} {type === 'Expenses' ? 'Job Cards' : 'Transactions'} ({transactions.length})</span>
                        <span className="text-sm">
                            Total: {formatCurrency(transactions.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || t.total_cost || 0)), 0))}
                        </span>
                    </div>
                </div>
                <div className="p-3">
                    {transactions.map((trans, idx) => (
                        <div
                            key={trans.id || idx}
                            className={`p-3 mb-2 rounded-lg border ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                                } hover:shadow-md transition-shadow`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                    <div className="text-xs text-gray-500 mb-1">
                                        {formatDate(trans.date || trans.opened_date)}
                                        {trans.invoice_week && ` - Week ${trans.invoice_week.split('_')[1]}`}
                                    </div>
                                    <div className="font-semibold text-sm text-gray-900">
                                        {trans.description || trans.complaint || trans.work_done || 'Job Card Details'}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        Ref: <span className="font-mono">{trans.doc_no || trans.job_card_no}</span>
                                    </div>
                                </div>
                                <div className="text-right ml-3 text-nowrap">
                                    <div className="font-bold text-lg text-[#FF5733]">
                                        {formatCurrency(Math.abs(parseFloat(trans.amount || trans.total_cost || 0)))}
                                    </div>
                                    <div className="text-xs text-gray-500">{trans.type || 'JC'}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Handler for toggling transaction tooltip on click
    const handleTransactionClick = (e, transactions, type) => {
        if (transactions && transactions.length > 0) {
            const rect = e.currentTarget.getBoundingClientRect();
            // Toggle: if already showing same transaction, hide it; otherwise show the new one
            if (hoveredTransaction.show && hoveredTransaction.type === type && hoveredTransaction.x === rect.right) {
                setHoveredTransaction({ show: false, transactions: [], type: '', x: 0, y: 0 });
            } else {
                setHoveredTransaction({
                    show: true,
                    transactions,
                    type,
                    x: rect.right,
                    y: rect.top
                });
            }
        }
        e.stopPropagation();
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setHoveredTransaction({ show: false, transactions: [], type: '', x: 0, y: 0 });
        };

        if (hoveredTransaction.show) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [hoveredTransaction.show]);

    const ValidationErrorDialog = () => (
        <Dialog open={showErrorDialog} handler={() => setShowErrorDialog(false)} size="md" className="outline-none">
            <DialogHeader className="flex items-center gap-2 text-red-600 border-b border-gray-100">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <Typography variant="h5">Action Required</Typography>
            </DialogHeader>
            <DialogBody className="max-h-[60vh] overflow-y-auto pt-4">
                <Typography variant="paragraph" color="blue-gray" className="mb-4 font-medium">
                    {errorDetails.message}
                </Typography>
                {errorDetails.errors.length > 0 && (
                    <div className="space-y-3">
                        {errorDetails.errors.map((err, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100 shadow-sm">
                                <Chip size="sm" variant="gradient" color="red" value={err.plate_no} className="mt-0.5" />
                                <Typography variant="small" color="red" className="font-normal flex-1">
                                    {err.error}
                                </Typography>
                            </div>
                        ))}
                    </div>
                )}
            </DialogBody>
            <DialogFooter className="border-t border-gray-100 bg-gray-50/50">
                <Button variant="text" color="blue-gray" onClick={() => setShowErrorDialog(false)} className="mr-1">
                    Dismiss
                </Button>
                <Button variant="gradient" color="red" onClick={() => setShowErrorDialog(false)}>
                    Close
                </Button>
            </DialogFooter>
        </Dialog>
    );

    // Loss transactions (LSS sub-type) grouped by car; losses are credits, so amount = credit - debit
    const getLossesByPlate = () => (reportData?.cars || [])
        .map(car => ({
            plate: car.plate_no,
            losses: (car.transactions?.losses || []).map(l => ({
                ...l,
                lossAmount: (l.credit || 0) - (l.debit || 0)
            }))
        }))
        .filter(group => group.losses.length > 0)
        .map(group => ({
            ...group,
            total: group.losses.reduce((sum, l) => sum + l.lossAmount, 0)
        }));

    const formatLossDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'gray';
            case 'PROCESSED': return 'blue';
            case 'APPROVED': return 'green';
            case 'PAID': return 'purple';
            default: return 'gray';
        }
    };

    // Builds the file on the server from the saved figures, stores it in Google Drive,
    // then downloads that stored copy, so what is downloaded is exactly what was saved.
    const handleSaveAndDownload = async (format) => {
        if (!reportData || exportingFormat) return;
        setExportingFormat(format);
        setExportError('');
        setSavedNotice(null);
        try {
            const saved = await generateSavedReport({
                token,
                ownerCode: selectedOwner,
                weekKey: selectedWeek,
                format,
                layout: format === 'pdf' ? layoutMode : undefined
            });
            await downloadSavedReport({ token, report: saved });
            setSavedNotice(saved);
        } catch (error) {
            setExportError(error.message);
        } finally {
            setExportingFormat(null);
        }
    };

    return (
        <>
            {/* Transaction Tooltip */}
            {hoveredTransaction.show && (
                <TransactionTooltip
                    transactions={hoveredTransaction.transactions}
                    type={hoveredTransaction.type}
                    x={hoveredTransaction.x}
                    y={hoveredTransaction.y}
                />
            )}

            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-3 sm:p-6 lg:p-8 text-sm screen-only">
                <div className="w-full mx-auto lg:px-4">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-3xl sm:text-4xl font-bold text-[#2a2e30] mb-2">Weekly Remittance Report</h1>
                        <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                        <p className="text-sm text-[#2a2e30] opacity-80">
                            Calculate, review, approve and export an owner&apos;s weekly remittance
                        </p>
                        <div className="mt-4 flex justify-center">
                            <Link
                                to={savedReportsLink({ ownerCode: selectedOwner, weekKey: selectedWeek })}
                                className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
                            >
                                <FontAwesomeIcon icon={faFolderOpen} />
                                Open Saved Reports
                            </Link>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 md:items-start">
                            <div>
                                <label htmlFor="remittance-owner" className="block mb-2 text-sm font-semibold text-[#2a2e30]">
                                    Owner
                                </label>
                                <Select
                                    inputId="remittance-owner"
                                    name="owner"
                                    options={owners.map(owner => ({
                                        value: owner.code,
                                        label: `${(owner.name || '').toUpperCase()} (${owner.code})`
                                    }))}
                                    value={selectedOwner ? { value: selectedOwner, label: (owners.find(o => o.code === selectedOwner)?.name || selectedOwner).toUpperCase() } : null}
                                    onChange={(option) => setSelectedOwner(option?.value || '')}
                                    isClearable
                                    placeholder="Select an owner..."
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div>
                                <label htmlFor="remittance-week" className="block mb-2 text-sm font-semibold text-[#2a2e30]">
                                    Week
                                </label>
                                <Select
                                    inputId="remittance-week"
                                    name="week"
                                    options={weeks.map(week => ({
                                        value: week.week,
                                        label: `${week.week} (${formatWeekRange(week)})`
                                    }))}
                                    value={selectedWeek ? { value: selectedWeek, label: selectedWeek } : null}
                                    onChange={(option) => setSelectedWeek(option?.value || '')}
                                    isClearable
                                    placeholder="Select a week..."
                                    styles={customSelectStyles}
                                />
                                {selectedWeekInfo && (
                                    <p className="mt-1 text-xs text-gray-500">{formatWeekRange(selectedWeekInfo, true)}</p>
                                )}
                            </div>

                            <div className="flex gap-2 md:pt-7">
                                <button
                                    type="button"
                                    onClick={requestCalculate}
                                    disabled={!selectedOwner || !selectedWeek || loading}
                                    className={`${BTN_PRIMARY} flex-1 md:flex-none`}
                                >
                                    <FontAwesomeIcon icon={loading ? faSpinner : faCalculator} spin={loading} />
                                    {loading ? 'Loading...' : (reportData ? 'Recalculate' : 'Calculate Report')}
                                </button>
                                {reportData && (
                                    <button
                                        type="button"
                                        onClick={fetchReport}
                                        disabled={loading}
                                        className={`${BTN_SECONDARY} flex-1 md:flex-none`}
                                        title="Reload the saved figures without recalculating"
                                    >
                                        <FontAwesomeIcon icon={faRotateRight} />
                                        Refresh
                                    </button>
                                )}
                            </div>
                        </div>
                        {selectedOwner && selectedWeek && reportMissing && !loading && (
                            <p className="mt-4 text-sm text-gray-600">
                                Week {selectedWeek} has not been calculated for this owner yet. Click <strong>Calculate Report</strong> to build it.
                            </p>
                        )}
                    </div>

                    {/* Payment Batch Dialog removed and moved to dedicated Payment Allocation page */}

                    {/* Modern Summary Cards */}
                    {reportData && (
                        <>
                            {/* Top summary cards removed per request */}

                            {/* Print Header - Only visible when printing */}
                            <div className="print-header" style={{ display: 'none' }}>
                                <div className="flex items-center justify-between border-b-2 border-[#FF5733] pb-3">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={companyLogoUrl}
                                            alt="Company Logo"
                                            className="h-12 w-12 object-contain"
                                            crossOrigin="anonymous"
                                        />
                                        <div>
                                            <div className="company-logo">{companyInfo.company_name || 'COSMO FLEETS'}</div>
                                            <div className="text-xs text-slate-500">
                                                {[companyInfo.company_phone, companyInfo.company_email].filter(Boolean).join(' | ')}
                                            </div>
                                            {companyInfo.company_address && (
                                                <div className="text-xs text-slate-500 whitespace-pre-line">{companyInfo.company_address}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="report-title">Weekly Remittance Report</div>
                                </div>
                                <div className="report-info">
                                    <div className="info-item">
                                        <div className="info-label">Owner</div>
                                        <div className="info-value">
                                            {owners.find(o => o.owner_code === selectedOwner)?.owner_name || selectedOwner}
                                        </div>
                                        <div className="info-value" style={{ fontSize: '9pt', color: '#666' }}>
                                            ({selectedOwner})
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <div className="info-label">Week Period</div>
                                        <div className="info-value">
                                            {weeks.find(w => w.week_key === selectedWeek)?.week_start_date &&
                                                new Date(weeks.find(w => w.week_key === selectedWeek)?.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            {' - '}
                                            {weeks.find(w => w.week_key === selectedWeek)?.week_end_date &&
                                                (() => { const d = new Date(weeks.find(w => w.week_key === selectedWeek)?.week_end_date); d.setDate(d.getDate() - 1); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })()}
                                        </div>
                                        <div className="info-value" style={{ fontSize: '9pt', color: '#666' }}>
                                            ({selectedWeek})
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <div className="info-label">Generated</div>
                                        <div className="info-value">
                                            {new Date().toLocaleDateString('en-GB')} {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        <div className="info-value" style={{ fontSize: '9pt', color: '#666' }}>
                                            By: {user?.username || 'System'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Report toolbar: layout tabs, export, selection */}
                            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-6 no-print">
                                <div className="flex flex-col-reverse gap-3 border-b border-gray-200 mb-4 md:flex-row md:items-end md:justify-between">
                                    <div className="flex overflow-x-auto" role="tablist" aria-label="Report layout">
                                        {LAYOUT_TABS.map(({ key, label, icon }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                role="tab"
                                                aria-selected={layoutMode === key}
                                                aria-label={label}
                                                onClick={() => setLayoutMode(key)}
                                                className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${layoutMode === key
                                                    ? 'border-[#FF5733] text-[#FF5733]'
                                                    : 'border-transparent text-gray-600 hover:text-[#2a2e30]'
                                                    }`}
                                            >
                                                <span className="md:hidden inline-block"><FontAwesomeIcon icon={icon} className="w-5 h-5" /></span>
                                                <span className="hidden md:inline">{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 pb-3">
                                        <button
                                            type="button"
                                            onClick={() => handleSaveAndDownload('csv')}
                                            disabled={!!exportingFormat || loading}
                                            className={`${BTN_SECONDARY} flex-1 md:flex-none`}
                                            title="Save a CSV copy to Google Drive and download it"
                                        >
                                            <FontAwesomeIcon icon={exportingFormat === 'csv' ? faSpinner : faFileExcel} spin={exportingFormat === 'csv'} />
                                            {exportingFormat === 'csv' ? 'Saving...' : 'Export CSV'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSaveAndDownload('pdf')}
                                            disabled={!!exportingFormat || loading}
                                            className={`${BTN_PRIMARY} flex-1 md:flex-none`}
                                            title={`Save a ${layoutMode} layout PDF to Google Drive and download it`}
                                        >
                                            <FontAwesomeIcon icon={exportingFormat === 'pdf' ? faSpinner : faFilePdf} spin={exportingFormat === 'pdf'} />
                                            {exportingFormat === 'pdf' ? 'Saving...' : 'Export PDF'}
                                        </button>
                                    </div>
                                </div>

                                {reportData.cars.length > 0 && (
                                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                        <label htmlFor="selectAll" className="flex items-center gap-3 cursor-pointer text-sm font-semibold text-[#2a2e30]">
                                            <input
                                                type="checkbox"
                                                id="selectAll"
                                                checked={reportData.cars.length > 0 && selectedCars.size === reportData.cars.length}
                                                onChange={toggleSelectAll}
                                                className="w-4 h-4 cursor-pointer accent-[#FF5733]"
                                            />
                                            Select all cars ({selectedCars.size}/{reportData.cars.length})
                                        </label>

                                        {selectedCars.size > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                <button type="button" onClick={handleBulkProcess} className={`${BTN_OUTLINE} flex-1 md:flex-none`}>
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    Process Selected ({selectedCars.size})
                                                </button>
                                                <button type="button" onClick={handleBulkApprove} className={`${BTN_PRIMARY} flex-1 md:flex-none`}>
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    Approve Selected ({selectedCars.size})
                                                </button>
                                                <button type="button" onClick={() => setSelectedCars(new Set())} className={`${BTN_SECONDARY} flex-1 md:flex-none`}>
                                                    Clear Selection
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {exportError && (
                                    <div role="alert" className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-500">
                                        <span className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
                                            <span>{exportError}</span>
                                        </span>
                                        <button type="button" onClick={() => setExportError('')} aria-label="Dismiss" className="text-red-400 hover:text-red-600">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                )}
                                {savedNotice && (
                                    <div className="mt-4 flex flex-col gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 md:flex-row md:items-center md:justify-between">
                                        <span className="flex items-center gap-2">
                                            <FontAwesomeIcon icon={faCloudArrowUp} />
                                            Saved to Google Drive as <strong className="break-all">{savedNotice.file_name}</strong>
                                        </span>
                                        <Link
                                            to={savedReportsLink({ ownerCode: savedNotice.owner_code, weekKey: savedNotice.week_key })}
                                            className="font-semibold text-[#FF5733] hover:underline"
                                        >
                                            Email or share it from Saved Reports
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {/* Desktop Table View - Columns Layout (Cars as Columns) */}
                            {layoutMode === 'columns' && (
                                <Card className="hidden lg:block w-full rounded-2xl shadow-xl overflow-hidden columns-print-layout">
                                    <CardBody className="p-0">
                                        <div
                                            className="overflow-x-scroll overflow-y-visible w-full"
                                            style={{
                                                scrollbarWidth: 'auto',
                                                scrollbarColor: '#FF5733 #f1f1f1',
                                                WebkitOverflowScrolling: 'touch'
                                            }}
                                        >
                                            <style jsx>{`
                                        div::-webkit-scrollbar {
                                            height: 12px;
                                        }
                                        div::-webkit-scrollbar-track {
                                            background: #f1f1f1;
                                            border-radius: 10px;
                                        }
                                        div::-webkit-scrollbar-thumb {
                                            background: #FF5733;
                                            border-radius: 10px;
                                        }
                                        div::-webkit-scrollbar-thumb:hover {
                                            background: #e64a2e;
                                        }
                                    `}</style>
                                            <table className="w-auto table-auto text-left border-collapse min-w-full">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                                        <th className="p-3 border-r border-gray-200 sticky left-0 bg-gray-50 z-10 min-w-[60px] text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={reportData.cars.length > 0 && selectedCars.size === reportData.cars.length}
                                                                onChange={toggleSelectAll}
                                                                className="w-4 h-4 cursor-pointer"
                                                            />
                                                        </th>
                                                        <th className="p-3 border-r border-gray-200 sticky left-[60px] bg-gray-50 z-10 min-w-[180px]">
                                                            Metric
                                                        </th>
                                                        {reportData.cars.map((car) => (
                                                            <th key={car.plate_no} className="p-3 border-r border-gray-200 min-w-[180px] text-right whitespace-nowrap">
                                                                <Link to={`/cars/${car.plate_no}`} className="hover:underline block" target="_blank" rel="noopener noreferrer">
                                                                    <Typography variant="small" className="font-bold text-[#FF5733]">
                                                                        {car.plate_no}
                                                                    </Typography>
                                                                    <Typography variant="small" className="text-gray-500 font-normal">
                                                                        {car.make} {car.model}
                                                                    </Typography>
                                                                </Link>
                                                            </th>
                                                        ))}
                                                        <th className="p-3 bg-[#fff1ec] text-[#FF5733] min-w-[180px] text-right font-bold whitespace-nowrap">
                                                            TOTALS
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Status Row */}
                                                    <tr className="bg-gray-50">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-gray-50 z-10 text-center">
                                                            {/* Selection column - empty in data rows */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-gray-50 z-10 whitespace-nowrap">
                                                            Status
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-right whitespace-nowrap">
                                                                <div className="flex flex-col gap-1 items-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedCars.has(car.id)}
                                                                        onChange={() => toggleCarSelection(car.id)}
                                                                        className="w-4 h-4 cursor-pointer"
                                                                    />
                                                                    <Chip
                                                                        value={car.status}
                                                                        color={getStatusColor(car.status)}
                                                                        size="sm"
                                                                        className="w-fit"
                                                                    />
                                                                    {car.is_locked && (
                                                                        <Chip
                                                                            value="Locked"
                                                                            icon={<FontAwesomeIcon icon={faLock} />}
                                                                            color="amber"
                                                                            size="sm"
                                                                            className="w-fit"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-center">-</td>
                                                    </tr>

                                                    {/* Working Days Row */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Working Days
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-right whitespace-nowrap">
                                                                <div className="flex items-center justify-center">
                                                                    <span className="font-semibold text-[#2a2e30]">{workingDaysOverrides[car.plate_no] || car.working_days}</span>
                                                                </div>
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {reportData.totals.total_working_days}
                                                        </td>
                                                    </tr>

                                                    {/* Previous Balance Row */}
                                                    <tr className="bg-gray-50">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-gray-50 z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-gray-50 z-10 whitespace-nowrap">
                                                            Previous Balance
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-right whitespace-nowrap">
                                                                {formatCurrency(car.prev_balance)}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_prev_balance)}
                                                        </td>
                                                    </tr>

                                                    {/* Week Target Row */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Week Target
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-right text-gray-800 whitespace-nowrap">
                                                                {formatCurrency(car.week_target)}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_week_target)}
                                                        </td>
                                                    </tr>

                                                    {/* REMITTED Row */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            REMITTED
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td
                                                                key={car.plate_no}
                                                                className="p-3 border-r border-gray-200 text-right text-[#2a2e30] font-medium whitespace-nowrap cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.remittance, 'Remittance')}
                                                            >
                                                                {formatCurrency(car.wkr_total)}
                                                                {car.transactions?.remittance?.length > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">({car.transactions.remittance.length})</span>
                                                                )}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_wkr)}
                                                        </td>
                                                    </tr>

                                                    {/* Losses Row */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Losses
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td
                                                                key={car.plate_no}
                                                                className="p-3 border-r border-gray-200 text-right text-red-600 font-medium whitespace-nowrap cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.losses, 'Losses')}
                                                            >
                                                                {formatCurrency(car.losses_total)}
                                                                {car.transactions?.losses?.length > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">({car.transactions.losses.length})</span>
                                                                )}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_losses)}
                                                        </td>
                                                    </tr>

                                                    {/* Adjustments Row */}
                                                    <tr className="bg-gray-50">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-gray-50 z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-gray-50 z-10 whitespace-nowrap">
                                                            Adjustments
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-right whitespace-nowrap">
                                                                {formatCurrency(car.adjustments_total)}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_adjustments)}
                                                        </td>
                                                    </tr>

                                                    {/* Expenses Row */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Expenses
                                                        </td>
                                                        {reportData.cars.map((car) => {
                                                            const carJcs = jobCardsData.filter(jc => jc.plate_no === car.plate_no);
                                                            return (
                                                                <td
                                                                    key={car.plate_no}
                                                                    className="p-3 border-r border-gray-200 text-right text-[#2a2e30] font-medium whitespace-nowrap cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                    onClick={(e) => handleTransactionClick(e, carJcs, 'Expenses')}
                                                                >
                                                                    {formatCurrency(car.expenses_total)}
                                                                    {carJcs.length > 0 && (
                                                                        <span className="ml-1 text-xs text-gray-500">({carJcs.length})</span>
                                                                    )}
                                                                </td>
                                                            );
                                                        })}
                                                        <td
                                                            className="p-3 bg-[#FF5733] text-white font-bold text-center cursor-pointer hover:bg-[#E64D2E] transition-colors"
                                                            onClick={(e) => handleTransactionClick(e, jobCardsData, 'Expenses')}
                                                        >
                                                            {formatCurrency(reportData.totals.total_expenses)}
                                                            {jobCardsData.length > 0 && (
                                                                <span className="ml-1 text-xs opacity-75">({jobCardsData.length})</span>
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {/* Owner Payout Row (swapped) */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Owner Payout
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-center text-[#2a2e30] font-bold whitespace-nowrap">
                                                                {formatCurrency(car.owner_payout)}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_owner_payout)}
                                                        </td>
                                                    </tr>

                                                    {/* Net Income Row (swapped) */}
                                                    <tr className="bg-white">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-white z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-white z-10 whitespace-nowrap">
                                                            Net Income
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-center text-[#2a2e30] font-bold whitespace-nowrap">
                                                                {formatCurrency(car.net_income)}
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-[#2a2e30] font-bold text-right whitespace-nowrap">
                                                            {formatCurrency(reportData.totals.total_net_income)}
                                                        </td>
                                                    </tr>

                                                    {/* Actions Row */}
                                                    <tr className="bg-gray-100 no-print">
                                                        <td className="p-3 border-r border-gray-200 sticky left-0 bg-gray-100 z-10 text-center">
                                                            {/* Selection column */}
                                                        </td>
                                                        <td className="p-3 border-r border-gray-200 font-semibold sticky left-[60px] bg-gray-100 z-10 whitespace-nowrap">
                                                            Actions
                                                        </td>
                                                        {reportData.cars.map((car) => (
                                                            <td key={car.plate_no} className="p-3 border-r border-gray-200 text-center">
                                                                <div className="flex flex-col gap-1 items-center">
                                                                    {car.status === 'PENDING' && !car.is_locked && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleProcess(car.id)}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none w-full"
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                                            Process
                                                                        </Button>
                                                                    )}
                                                                    {car.status === 'PROCESSED' && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleApprove(car.id)}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none w-full"
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                                            Approve
                                                                        </Button>
                                                                    )}
                                                                    {car.status === 'APPROVED' && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setSelectedRemittanceId(car.id);
                                                                                setPaymentData(prev => ({ ...prev, amount: (car.owner_payout || 0).toString() }));
                                                                                setShowPaymentDialog(true);
                                                                            }}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none w-full" title="Record payment"
                                                                        >
<FontAwesomeIcon icon={faMoneyBillWave} />
                                                                        </Button>
                                                                    )}
                                                                    {car.is_locked && (
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outlined"
                                                                            onClick={() => handleUnlock(car.id)}
                                                                            className="border-gray-300 text-gray-700 w-full"
                                                                        >
                                                                            <FontAwesomeIcon icon={faUnlock} />
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outlined"
                                                                        onClick={() => {
                                                                            setSelectedRemittanceId(car.id);
                                                                            setShowVersionHistory(true);
                                                                        }}
                                                                        className="w-full"
                                                                    >
                                                                        <FontAwesomeIcon icon={faHistory} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        ))}
                                                        <td className="p-3 bg-[#fff1ec] text-center">-</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Desktop Table View - Rows Layout (Cars as Rows) */}
                            {layoutMode === 'rows' && (
                                <Card className="hidden lg:block w-full rounded-2xl shadow-xl overflow-hidden rows-print-layout">
                                    <CardBody className="p-0">
                                        <div className="overflow-x-auto">
                                            <table className="w-full table-auto text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                                        <th className="p-3 border-r border-gray-200 text-center w-12">
                                                            <input
                                                                type="checkbox"
                                                                checked={reportData.cars.length > 0 && selectedCars.size === reportData.cars.length}
                                                                onChange={toggleSelectAll}
                                                                className="w-4 h-4 cursor-pointer"
                                                            />
                                                        </th>
                                                        <th className="p-3 border-r border-gray-200">Plate No</th>
                                                        <th className="p-3 border-r border-gray-200">Make/Model</th>
                                                        <th className="p-3 border-r border-gray-200 text-center">Status</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Prev Bal</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">W/Target</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Remitted</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Losses</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Adj(s)</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Expenses</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Owner Payout</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Net Income</th>
                                                        <th className="p-3 border-r border-gray-200 text-right">Closing Bal</th>
                                                        <th className="p-3 text-center no-print">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.cars.map((car, index) => (
                                                        <tr key={car.plate_no} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="p-3 border-r border-gray-200 text-center w-12">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCars.has(car.id)}
                                                                    onChange={() => toggleCarSelection(car.id)}
                                                                    className="w-4 h-4 cursor-pointer"
                                                                />
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 font-semibold">
                                                                <Link to={`/cars/${car.plate_no}`} className="hover:underline text-[#FF5733]" target="_blank" rel="noopener noreferrer">
                                                                    {car.plate_no}
                                                                </Link>
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-sm">
                                                                {car.make} {car.model}
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-center">
                                                                <div className="flex flex-col gap-1 items-center">
                                                                    <Chip
                                                                        value={car.status}
                                                                        color={getStatusColor(car.status)}
                                                                        size="sm"
                                                                        className="w-fit"
                                                                    />
                                                                    {car.is_locked && (
                                                                        <Chip
                                                                            value="Locked"
                                                                            icon={<FontAwesomeIcon icon={faLock} />}
                                                                            color="amber"
                                                                            size="sm"
                                                                            className="w-fit"
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-center">
                                                                <div className="flex flex-col gap-1 items-center">
                                                                    <span className="font-semibold text-[#2a2e30]">{car.working_days}</span>
                                                                </div>
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm">{formatCurrency(car.prev_balance)}</td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm">{formatCurrency(car.week_target)}</td>
                                                            <td
                                                                className="p-3 border-r border-gray-200 text-right text-sm text-[#2a2e30] font-medium cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.remittance, 'Remittance')}
                                                            >
                                                                {formatCurrency(car.wkr_total)}
                                                                {car.transactions?.remittance?.length > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">({car.transactions.remittance.length})</span>
                                                                )}
                                                            </td>
                                                            <td
                                                                className="p-3 border-r border-gray-200 text-right text-sm text-red-600 font-medium cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.losses, 'Losses')}
                                                            >
                                                                {formatCurrency(car.losses_total)}
                                                                {car.transactions?.losses?.length > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">({car.transactions.losses.length})</span>
                                                                )}
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm">{formatCurrency(car.adjustments_total)}</td>
                                                            <td
                                                                className="p-3 border-r border-gray-200 text-right text-sm text-[#2a2e30] font-medium cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => {
                                                                    const carJcs = jobCardsData.filter(jc => jc.plate_no === car.plate_no);
                                                                    handleTransactionClick(e, carJcs, 'Expenses');
                                                                }}
                                                            >
                                                                {formatCurrency(car.expenses_total)}
                                                                {jobCardsData.filter(jc => jc.plate_no === car.plate_no).length > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">({jobCardsData.filter(jc => jc.plate_no === car.plate_no).length})</span>
                                                                )}
                                                            </td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm text-[#2a2e30] font-bold">{formatCurrency(car.owner_payout)}</td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm text-[#2a2e30] font-bold">{formatCurrency(car.net_income)}</td>
                                                            <td className="p-3 border-r border-gray-200 text-right text-sm text-[#2a2e30] font-bold">{formatCurrency(car.closing_balance || 0)}</td>
                                                            <td className="p-3 text-center no-print">
                                                                <div className="flex flex-wrap gap-1 justify-center">
                                                                    {car.status === 'PENDING' && !car.is_locked && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleProcess(car.id)}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none"
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                        </Button>
                                                                    )}
                                                                    {car.status === 'PROCESSED' && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleApprove(car.id)}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none"
                                                                        >
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                        </Button>
                                                                    )}
                                                                    {car.status === 'APPROVED' && (
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setSelectedRemittanceId(car.id);
                                                                                setPaymentData(prev => ({ ...prev, amount: (car.owner_payout || 0).toString() }));
                                                                                setShowPaymentDialog(true);
                                                                            }}
                                                                            className="!bg-[#FF5733] shadow-none hover:shadow-none" title="Record payment"
                                                                        >
<FontAwesomeIcon icon={faMoneyBillWave} />
                                                                        </Button>
                                                                    )}
                                                                    {car.is_locked && (
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outlined"
                                                                            onClick={() => handleUnlock(car.id)}
                                                                            className="border-gray-300 text-gray-700"
                                                                        >
                                                                            <FontAwesomeIcon icon={faUnlock} />
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outlined"
                                                                        onClick={() => {
                                                                            setSelectedRemittanceId(car.id);
                                                                            setShowVersionHistory(true);
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faHistory} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {/* Totals Row */}
                                                    <tr className="bg-[#fff1ec] text-[#2a2e30] font-bold border-t-2 border-[#FF5733]">
                                                        <td colSpan="4" className="p-3 text-center">TOTALS</td>
                                                        <td className="p-3 text-center">{reportData.totals.total_working_days}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_prev_balance)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_week_target)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_wkr)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_losses)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_adjustments)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_expenses)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_owner_payout)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_net_income)}</td>
                                                        <td className="p-3 text-right">{formatCurrency(reportData.totals.total_closing_balance || 0)}</td>
                                                        <td className="p-3">-</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Desktop Grid View - Per Car Layout */}
                            {layoutMode === 'grid' && (
                                <div className="hidden lg:block grid-print-layout">
                                    <div className="grid grid-cols-4 gap-6">
                                        {reportData.cars.map((car) => (
                                            <Card key={car.plate_no} className="shadow-lg hover:shadow-xl transition-shadow">
                                                <CardBody className="p-4">
                                                    {/* Vehicle Header */}
                                                    <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-[#FF5733]">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCars.has(car.id)}
                                                                    onChange={() => toggleCarSelection(car.id)}
                                                                    className="w-4 h-4 cursor-pointer"
                                                                />
                                                                <Link to={`/cars/${car.plate_no}`} className="hover:underline" target="_blank" rel="noopener noreferrer">
                                                                    <Typography variant="h5" className="font-bold text-[#FF5733] flex items-center gap-2">
                                                                        <FontAwesomeIcon icon={faCar} />
                                                                        {car.plate_no}
                                                                    </Typography>
                                                                </Link>
                                                            </div>
                                                            <Typography variant="small" className="text-gray-600 font-medium">
                                                                {car.make} {car.model}
                                                            </Typography>
                                                        </div>
                                                        <div className="flex flex-col gap-1 items-end">
                                                            <Chip
                                                                value={car.status}
                                                                color={getStatusColor(car.status)}
                                                                size="sm"
                                                                className="w-fit"
                                                            />
                                                            {car.is_locked && (
                                                                <Chip
                                                                    value="Locked"
                                                                    icon={<FontAwesomeIcon icon={faLock} />}
                                                                    color="amber"
                                                                    size="sm"
                                                                    className="w-fit"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Metrics Table - 2 columns (label left, amount right) */}
                                                    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                                                        <div className="grid grid-cols-2 gap-0">
                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Working Days
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 border-b border-gray-200 text-right">
                                                                <Typography variant="small" className="font-bold text-gray-800">
                                                                    {workingDaysOverrides[car.plate_no] || car.working_days}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Prev Balance
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 border-b border-gray-200 text-right">
                                                                <Typography variant="small" className="font-bold text-gray-800">
                                                                    {formatCurrency(car.prev_balance)}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Week Target
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 border-b border-gray-200 text-right">
                                                                <Typography variant="small" className="font-bold text-green-700">
                                                                    {formatCurrency(car.week_target)}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Remitted
                                                                </Typography>
                                                            </div>
                                                            <div
                                                                className="col-span-1 bg-white p-3 border-b border-gray-200 text-right cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.remittance, 'Remittance')}
                                                            >
                                                                <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                                    {formatCurrency(car.wkr_total)}
                                                                    {car.transactions?.remittance?.length > 0 && (
                                                                        <span className="ml-1 text-xs text-gray-500">({car.transactions.remittance.length})</span>
                                                                    )}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Losses
                                                                </Typography>
                                                            </div>
                                                            <div
                                                                className="col-span-1 bg-white p-3 border-b border-gray-200 text-right cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => handleTransactionClick(e, car.transactions?.losses, 'Losses')}
                                                            >
                                                                <Typography variant="small" className="font-bold text-red-600">
                                                                    {formatCurrency(car.losses_total)}
                                                                    {car.transactions?.losses?.length > 0 && (
                                                                        <span className="ml-1 text-xs text-gray-500">({car.transactions.losses.length})</span>
                                                                    )}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Adjustments
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 border-b border-gray-200 text-right">
                                                                <Typography variant="small" className="font-bold text-gray-800">
                                                                    {formatCurrency(car.adjustments_total)}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Expenses
                                                                </Typography>
                                                            </div>
                                                            <div
                                                                className="col-span-1 bg-white p-3 border-b border-gray-200 text-right cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                                onClick={(e) => {
                                                                    const carJcs = jobCardsData.filter(jc => jc.plate_no === car.plate_no);
                                                                    handleTransactionClick(e, carJcs, 'Expenses');
                                                                }}
                                                            >
                                                                <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                                    {formatCurrency(car.expenses_total)}
                                                                    {jobCardsData.filter(jc => jc.plate_no === car.plate_no).length > 0 && (
                                                                        <span className="ml-1 text-xs text-gray-500">({jobCardsData.filter(jc => jc.plate_no === car.plate_no).length})</span>
                                                                    )}
                                                                </Typography>
                                                            </div>


                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Net Income
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 border-b border-gray-200 text-right">
                                                                <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                                    {formatCurrency(car.net_income)}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-200">
                                                                <Typography variant="small" className="text-gray-600 font-medium">
                                                                    Closing Balance
                                                                </Typography>
                                                            </div>
                                                            <div className="col-span-1 bg-white p-3 text-right">
                                                                <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                                    {formatCurrency(car.closing_balance || 0)}
                                                                </Typography>
                                                            </div>

                                                            <div className="col-span-2 bg-gradient-to-r from-[#FF5733] to-[#e64a2e] p-3 rounded-none text-white flex justify-between items-center">
                                                                <Typography variant="small" className="text-white/90 font-medium">
                                                                    Owner Payout
                                                                </Typography>
                                                                <Typography variant="h6" className="font-bold">
                                                                    {formatCurrency(car.owner_payout)}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 no-print">
                                                        {car.status === 'PENDING' && !car.is_locked && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleProcess(car.id)}
                                                                className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1"
                                                            >
                                                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                                Process
                                                            </Button>
                                                        )}
                                                        {car.status === 'PROCESSED' && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleApprove(car.id)}
                                                                className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1"
                                                            >
                                                                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                                Approve
                                                            </Button>
                                                        )}
                                                        {car.status === 'APPROVED' && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedRemittanceId(car.id);
                                                                    setPaymentData(prev => ({ ...prev, amount: (car.owner_payout || 0).toString() }));
                                                                    setShowPaymentDialog(true);
                                                                }}
                                                                className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1" title="Record payment"
                                                            >
                                                                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1" />
                                                                Mark Paid
                                                            </Button>
                                                        )}
                                                        {car.is_locked && (
                                                            <Button
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => handleUnlock(car.id)}
                                                                className="border-gray-300 text-gray-700"
                                                            >
                                                                <FontAwesomeIcon icon={faUnlock} className="mr-1" />
                                                                Unlock
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setSelectedRemittanceId(car.id);
                                                                setShowVersionHistory(true);
                                                            }}
                                                            className="border-gray-400 text-gray-700"
                                                        >
                                                            <FontAwesomeIcon icon={faHistory} className="mr-1" />
                                                            History
                                                        </Button>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        ))}

                                        {/* Totals Summary Table */}
                                        <div className="mt-6 border border-gray-300 rounded-lg overflow-hidden bg-white">
                                            <div className="bg-gradient-to-r from-[#FF5733] to-[#e64a2e] text-white p-4 border-b">
                                                <Typography variant="h5" className="font-bold flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faCalculator} />
                                                    TOTALS SUMMARY
                                                </Typography>
                                            </div>
                                            <div className="grid grid-cols-2 gap-0">
                                                <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Working Days
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {reportData.totals.total_working_days}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Prev Balance
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {formatCurrency(reportData.totals.total_prev_balance)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Week Target
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-green-700">
                                                        {formatCurrency(reportData.totals.total_week_target)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Total Remitted
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(reportData.totals.total_wkr)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Total Losses
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-red-600">
                                                        {formatCurrency(reportData.totals.total_losses)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Adjustments
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {formatCurrency(reportData.totals.total_adjustments)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-r border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Total Expenses
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(reportData.totals.total_expenses)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-1 bg-gray-50 p-3 border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Net Income
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(reportData.totals.total_net_income)}
                                                    </Typography>
                                                </div>

                                                {/* Balance Before Payout removed from totals summary */}

                                                <div className="col-span-1 bg-gray-50 p-3 border-b border-gray-300">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Closing Balance
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(reportData.totals.total_closing_balance || 0)}
                                                    </Typography>
                                                </div>

                                                <div className="col-span-2 bg-gradient-to-r from-[#FF5733] to-[#e64a2e] p-3 rounded-none text-white flex justify-between items-center">
                                                    <Typography variant="small" className="font-medium">Total Owner Payout</Typography>
                                                    <Typography variant="h6" className="font-bold">{formatCurrency(reportData.totals.total_owner_payout)}</Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Print Summary Section - Only visible when printing */}
                            <div className="print-summary" style={{ display: 'none' }}>
                                <h3>SUMMARY</h3>
                                <div className="summary-grid">
                                    <div className="summary-item">
                                        <div className="summary-label">Working Days</div>
                                        <div className="summary-value">{reportData.totals.total_working_days}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Prev Balance</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_prev_balance)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Week Target</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_week_target)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Total Remitted</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_wkr)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Total Losses</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_losses)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Adjustments</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_adjustments)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Total Expenses</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_expenses)}</div>
                                    </div>
                                    <div className="summary-item">
                                        <div className="summary-label">Net Income</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_net_income)}</div>
                                    </div>
                                    {/* Balance Before Payout removed from print summary */}
                                    <div className="summary-item">
                                        <div className="summary-label">Closing Balance</div>
                                        <div className="summary-value">{formatCurrency(reportData.totals.total_closing_balance || 0)}</div>
                                    </div>
                                    <div className="summary-item summary-highlight">
                                        <div className="summary-label">TOTAL OWNER PAYOUT</div>
                                        <div className="summary-value" style={{ fontSize: '14pt' }}>{formatCurrency(reportData.totals.total_owner_payout)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Card View - Visible only on Mobile/Tablet */}
                            <div className="lg:hidden space-y-4 no-print">
                                {reportData.cars.map((car, index) => (
                                    <Card key={car.plate_no} className="shadow-md hover:shadow-lg transition-shadow">
                                        <CardBody className="p-4">
                                            {/* Vehicle Header */}
                                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCars.has(car.id)}
                                                            onChange={() => toggleCarSelection(car.id)}
                                                            className="w-4 h-4 cursor-pointer"
                                                        />
                                                        <Link to={`/cars/${car.plate_no}`} className="hover:underline" target="_blank" rel="noopener noreferrer">
                                                            <Typography variant="h6" className="font-bold text-[#FF5733] flex items-center gap-2">
                                                                <FontAwesomeIcon icon={faCar} className="text-lg" />
                                                                {car.plate_no}
                                                            </Typography>
                                                        </Link>
                                                    </div>
                                                    <Typography variant="small" className="text-gray-600">
                                                        {car.make} {car.model}
                                                    </Typography>
                                                </div>
                                                <div className="flex flex-col gap-1 items-end">
                                                    <Chip
                                                        value={car.status}
                                                        color={getStatusColor(car.status)}
                                                        size="sm"
                                                        className="w-fit"
                                                    />
                                                    {car.is_locked && (
                                                        <Chip
                                                            value="Locked"
                                                            icon={<FontAwesomeIcon icon={faLock} />}
                                                            color="amber"
                                                            size="sm"
                                                            className="w-fit"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Metrics Grid */}
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Working Days
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold">
                                                        {workingDaysOverrides[car.plate_no] ?? car.working_days}
                                                    </Typography>
                                                </div>

                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Prev Balance
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-gray-800">
                                                        {formatCurrency(car.prev_balance)}
                                                    </Typography>
                                                </div>

                                                <div className="bg-green-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Week Target
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-green-700">
                                                        {formatCurrency(car.week_target)}
                                                    </Typography>
                                                </div>

                                                <div
                                                    className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                    onClick={(e) => handleTransactionClick(e, car.transactions?.remittance, 'Remittance')}
                                                >
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Remitted
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(car.wkr_total)}
                                                        {car.transactions?.remittance?.length > 0 && (
                                                            <span className="ml-1 text-xs text-gray-500">({car.transactions.remittance.length})</span>
                                                        )}
                                                    </Typography>
                                                </div>

                                                <div
                                                    className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-[#fff1ec] transition-colors"
                                                    onClick={(e) => handleTransactionClick(e, car.transactions?.losses, 'Losses')}
                                                >
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Losses
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-red-600">
                                                        {formatCurrency(car.losses_total)}
                                                        {car.transactions?.losses?.length > 0 && (
                                                            <span className="ml-1 text-xs text-gray-500">({car.transactions.losses.length})</span>
                                                        )}
                                                    </Typography>
                                                </div>

                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Adjustments
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-gray-800">
                                                        {formatCurrency(car.adjustments_total)}
                                                    </Typography>
                                                </div>

                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Expenses
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(car.expenses_total)}
                                                    </Typography>
                                                </div>

                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Owner Payout
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(car.owner_payout)}
                                                    </Typography>
                                                </div>

                                                <div className="bg-gray-50 p-3 rounded-lg">
                                                    <Typography variant="small" className="text-gray-600 mb-1 font-medium">
                                                        Net Income
                                                    </Typography>
                                                    <Typography variant="small" className="font-bold text-[#2a2e30]">
                                                        {formatCurrency(car.net_income)}
                                                    </Typography>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                                                {car.status === 'PENDING' && !car.is_locked && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleProcess(car.id)}
                                                        className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1"
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                        Process
                                                    </Button>
                                                )}
                                                {car.status === 'PROCESSED' && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleApprove(car.id)}
                                                        className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1"
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                                        Approve
                                                    </Button>
                                                )}
                                                {car.status === 'APPROVED' && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedRemittanceId(car.id);
                                                            setPaymentData(prev => ({ ...prev, amount: (car.owner_payout || 0).toString() }));
                                                            setShowPaymentDialog(true);
                                                        }}
                                                        className="!bg-[#FF5733] shadow-none hover:shadow-none flex-1" title="Record payment"
                                                    >
                                                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1" />
                                                        Mark Paid
                                                    </Button>
                                                )}
                                                {car.is_locked && (
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        onClick={() => handleUnlock(car.id)}
                                                        className="border-gray-300 text-gray-700"
                                                    >
                                                        <FontAwesomeIcon icon={faUnlock} className="mr-1" />
                                                        Unlock
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setSelectedRemittanceId(car.id);
                                                        setShowVersionHistory(true);
                                                    }}
                                                    className="border-gray-400 text-gray-700"
                                                >
                                                    <FontAwesomeIcon icon={faHistory} className="mr-1" />
                                                    History
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))}

                                {/* Mobile Totals Card */}
                                <Card className="bg-gradient-to-br from-[#FF5733] to-[#e64a2e] text-white shadow-lg">
                                    <CardBody className="p-4">
                                        <Typography variant="h6" className="font-bold mb-4 flex items-center gap-2">
                                            <FontAwesomeIcon icon={faCalculator} />
                                            TOTALS
                                        </Typography>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Working Days
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {reportData.totals.total_working_days}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Prev Balance
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_prev_balance)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Week Target
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_week_target)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Remitted
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_wkr)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Losses
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_losses)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Adjustments
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_adjustments)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Expenses
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_expenses)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Owner Payout
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_owner_payout)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Net Income
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_net_income)}
                                                </Typography>
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                                                <Typography variant="small" className="text-white/80 mb-1">
                                                    Closing Balance
                                                </Typography>
                                                <Typography variant="small" className="font-bold">
                                                    {formatCurrency(reportData.totals.total_closing_balance || 0)}
                                                </Typography>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div >
                        </>
                    )}

                    {/* Losses Section */}
                    {
                        reportData && getLossesByPlate().length > 0 && (
                            <Card className="mt-6 rounded-2xl shadow-xl">
                                <CardBody className="p-6">
                                    <Typography variant="h5" className="mb-4 text-[#2a2e30] font-bold flex items-center gap-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        Losses Detail
                                    </Typography>

                                    {getLossesByPlate().map(({ plate, losses, total }) => (
                                        <div key={plate} className="mb-6">
                                            <Typography variant="h6" className="mb-3 text-[#2a2e30] font-bold">
                                                {plate}
                                            </Typography>
                                            <div className="overflow-x-auto">
                                                <table className="w-full border-collapse text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                                            <th className="border border-gray-300 p-2 text-left">Doc #</th>
                                                            <th className="border border-gray-300 p-2 text-center">Date</th>
                                                            <th className="border border-gray-300 p-2 text-left">Loss Type</th>
                                                            <th className="border border-gray-300 p-2 text-left">Reference</th>
                                                            <th className="border border-gray-300 p-2 text-left w-2/5">Details</th>
                                                            <th className="border border-gray-300 p-2 text-right">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {losses.map((loss, idx) => (
                                                            <tr key={loss.id || `${plate}-${idx}`} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                <td className="border border-gray-300 p-2 font-semibold">{loss.doc_no || '-'}</td>
                                                                <td className="border border-gray-300 p-2 text-center">{formatLossDate(loss.date)}</td>
                                                                <td className="border border-gray-300 p-2 text-xs">{loss.type_description || loss.description || loss.type || '-'}</td>
                                                                <td className="border border-gray-300 p-2 text-xs">{loss.reference || '-'}</td>
                                                                <td className="border border-gray-300 p-2 text-xs">{loss.notes || '-'}</td>
                                                                <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(loss.lossAmount)}</td>
                                                            </tr>
                                                        ))}
                                                        <tr>
                                                            <td colSpan="5" className="border border-gray-300 p-2 text-right font-bold">TOTAL</td>
                                                            <td className="border border-gray-300 p-2 text-right font-bold bg-[#FF5733] text-white">{formatCurrency(total)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </CardBody>
                            </Card>
                        )
                    }

                    {/* Job Cards Section */}
                    {
                        reportData && jobCardsData && jobCardsData.length > 0 && (
                            <Card className="mt-6 rounded-2xl shadow-xl">
                                <CardBody className="p-6">
                                    <Typography variant="h5" className="mb-4 text-[#2a2e30] font-bold flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCar} />
                                        Job Cards Detail
                                    </Typography>

                                    {/* Group by plate */}
                                    {Object.entries(
                                        jobCardsData.reduce((acc, jc) => {
                                            if (!acc[jc.plate_no]) acc[jc.plate_no] = [];
                                            acc[jc.plate_no].push(jc);
                                            return acc;
                                        }, {})
                                    ).map(([plate, jobCards]) => (
                                        <div key={plate} className="mb-6">
                                            <Typography variant="h6" className="mb-3 text-[#2a2e30] font-bold">
                                                {plate}
                                            </Typography>
                                            <div className="overflow-x-auto">
                                                <table className="w-full border-collapse text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                                            <th className="border border-gray-300 p-2 text-left">Job Card #</th>
                                                            <th className="border border-gray-300 p-2 text-center">Opened</th>
                                                            <th className="border border-gray-300 p-2 text-left">Item/Charge</th>
                                                            <th className="border border-gray-300 p-2 text-right">Qty</th>
                                                            <th className="border border-gray-300 p-2 text-right">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {jobCards.map((jc, jcIdx) => {
                                                            const allItems = [
                                                                ...(jc.parts || []).map(p => ({
                                                                    type: 'part',
                                                                    name: p.part_name || 'N/A',
                                                                    quantity: p.quantity_used,
                                                                    amount: p.total_cost
                                                                })),
                                                                ...(jc.charges || []).map(c => ({
                                                                    type: 'charge',
                                                                    name: `${c.charge_code || c.code || 'Charge'} - ${c.charge_name || c.name || ''}`.trim(),
                                                                    quantity: 1,
                                                                    amount: c.total_amount
                                                                }))
                                                            ];

                                                            if (allItems.length === 0) {
                                                                return (
                                                                    <tr key={jc.id} className={jcIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                        <td className="border border-gray-300 p-2 font-semibold">
                                                                            <a href={`/job-cards/${jc.id}`} className="text-[#FF5733] hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                                                                                {jc.job_card_no}
                                                                            </a>
                                                                        </td>
                                                                        <td className="border border-gray-300 p-2 text-center">
                                                                            {new Date(jc.opened_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                        </td>
                                                                        <td className="border border-gray-300 p-2">-</td>
                                                                        <td className="border border-gray-300 p-2 text-right">-</td>
                                                                        <td className="border border-gray-300 p-2 text-right font-bold">{formatCurrency(jc.total_cost)}</td>
                                                                    </tr>
                                                                );
                                                            }

                                                            return [
                                                                ...allItems.map((item, itemIdx) => (
                                                                    <tr key={`${jc.id}-${itemIdx}`} className={jcIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                        {itemIdx === 0 && (
                                                                            <>
                                                                                <td rowSpan={allItems.length + 1} className="border border-gray-300 p-2 font-semibold align-top">
                                                                                    <a href={`/job-cards/${jc.id}`} className="text-[#FF5733] hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                                                                                        {jc.job_card_no}
                                                                                    </a>
                                                                                </td>
                                                                                <td rowSpan={allItems.length + 1} className="border border-gray-300 p-2 text-center align-top">
                                                                                    {new Date(jc.opened_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                        <td className="border border-gray-300 p-2 text-xs">
                                                                            {item.name}
                                                                        </td>
                                                                        <td className="border border-gray-300 p-2 text-right">{item.quantity}</td>
                                                                        <td className="border border-gray-300 p-2 text-right font-mono">{formatCurrency(item.amount)}</td>
                                                                    </tr>
                                                                )),
                                                                <tr key={`${jc.id}-total`} className={jcIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                                    <td colSpan="2" className="border border-gray-300 p-2 text-right font-bold">TOTAL</td>
                                                                    <td className="border border-gray-300 p-2 text-right font-bold bg-[#FF5733] text-white">{formatCurrency(jc.total_cost)}</td>
                                                                </tr>
                                                            ];
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </CardBody>
                            </Card>
                        )
                    }

                    {/* Payment Dialog - Single Car Payment */}
                    <Dialog open={showPaymentDialog} handler={() => setShowPaymentDialog(false)} size="lg">
                        <DialogHeader>Mark Remittance as Paid</DialogHeader>
                        <DialogBody divider className="space-y-4 max-h-96 overflow-y-auto">
                            {/* Payment Date */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Payment Date
                                    <span className="text-red-500 ml-1">*</span>
                                </Typography>
                                <Input
                                    type="date"
                                    value={paymentData.date}
                                    onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                                    className="w-full"
                                />
                            </div>

                            {/* Payment Method */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Payment Method
                                    <span className="text-red-500 ml-1">*</span>
                                </Typography>
                                <Select
                                    value={{ value: paymentData.payment_method, label: paymentData.payment_method === 'MPESA' ? 'M-Pesa' : paymentData.payment_method === 'BANK_TRANSFER' ? 'Bank Transfer' : paymentData.payment_method === 'CHEQUE' ? 'Cheque' : paymentData.payment_method }}
                                    options={[
                                        { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
                                        { value: 'CHEQUE', label: 'Cheque' },
                                        { value: 'MPESA', label: 'M-Pesa' },
                                        { value: 'CASH', label: 'Cash' },
                                        { value: 'OTHER', label: 'Other' }
                                    ]}
                                    onChange={(option) => setPaymentData({ ...paymentData, payment_method: option.value })}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#d1d5db',
                                        })
                                    }}
                                    isSearchable={false}
                                    menuPlacement="auto"
                                />
                            </div>

                            {/* Cashbook Account */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Cashbook Account
                                    <span className="text-red-500 ml-1">*</span>
                                </Typography>
                                <Select
                                    value={
                                        paymentData.cashbook_account_id && cashbookAccounts?.length > 0
                                            ? cashbookAccounts
                                                .map(acc => ({
                                                    value: acc.id,
                                                    label: `${acc.account_name} (${acc.account_type})`
                                                }))
                                                .find(opt => opt.value === paymentData.cashbook_account_id)
                                            : null
                                    }
                                    options={cashbookAccounts?.map(acc => ({
                                        value: acc.id,
                                        label: `${acc.account_name} (${acc.account_type})`
                                    })) || []}
                                    onChange={(option) => setPaymentData({ ...paymentData, cashbook_account_id: option.value })}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#d1d5db',
                                        })
                                    }}
                                    isSearchable={true}
                                    menuPlacement="auto"
                                    placeholder="Select cashbook account..."
                                />
                            </div>

                            {/* Amount */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Amount
                                    <span className="text-red-500 ml-1">*</span>
                                </Typography>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentData.amount}
                                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                                    placeholder="0.00"
                                    className="w-full"
                                />
                            </div>

                            {/* Batch Reference (Auto-generated, Read-only) */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Batch Reference (Auto-generated)
                                </Typography>
                                <Input
                                    type="text"
                                    value={paymentData.reference}
                                    disabled
                                    placeholder="Generated automatically"
                                    className="w-full bg-gray-100"
                                />
                            </div>

                            {/* Notes */}
                            <div>
                                <Typography variant="small" className="font-semibold mb-2">
                                    Notes (Optional)
                                </Typography>
                                <Textarea
                                    value={paymentData.notes}
                                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                                    placeholder="Add any notes about this payment..."
                                    className="w-full"
                                />
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            <Button variant="text" onClick={() => setShowPaymentDialog(false)} className="mr-2">
                                Cancel
                            </Button>
                            <Button onClick={handleMarkPaid} className="bg-[#FF5733]">
                                Confirm Payment
                            </Button>
                        </DialogFooter>
                    </Dialog>

                    <ValidationErrorDialog />

                    <ConfirmModal
                        isOpen={!!confirmDialog}
                        title={confirmDialog?.title}
                        description={confirmDialog?.description}
                        typedConfirmLabel={confirmDialog?.typedConfirmLabel}
                        typedConfirmValue={confirmTyped}
                        onTypedConfirmChange={(value) => { setConfirmTyped(value); setConfirmError(''); }}
                        error={confirmError}
                        confirmText={confirmDialog?.confirmText}
                        variant={confirmDialog?.variant}
                        onCancel={() => closeConfirm(false)}
                        onConfirm={() => closeConfirm(true)}
                    />

                    {/* Version History Modal */}
                    {
                        showVersionHistory && selectedRemittanceId && (
                            <VersionHistoryModal
                                remittanceId={selectedRemittanceId}
                                open={showVersionHistory}
                                onClose={() => {
                                    setShowVersionHistory(false);
                                    setSelectedRemittanceId(null);
                                }}
                                onRestore={fetchReport}
                                token={token}
                                formatCurrency={formatCurrency}
                                username={user?.username || 'system'}
                            />
                        )
                    }
                </div >
            </div >
        </>
    );
};

export default OwnerRemittanceReport;
